import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardMedia,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScoreIcon from '@mui/icons-material/Score';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { MilitaryTechOutlined, MoreVert, ScoreOutlined, SportsGolfOutlined } from '@mui/icons-material';
import { red } from '@mui/material/colors';

// -----------------------------
// DTOs según api.json
// -----------------------------

export interface IMiniGameDto {
    id: number;
    tournamentId: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    displayOrder: number;
    isActive: boolean;
}

export interface ITournamentDto {
    id: number;
    name: string | null;
    imageUrl: string | null;
    startDate: string; // datetime
    endDate: string;   // datetime
    isActive: boolean;
}

export interface IPlayerDto {
    id: number;
    tournamentId: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    sex: string | null;
    createdAt: string; // datetime
}

export interface IPlayerMiniGameScoreDto {
    id: number;
    tournamentId: number;
    playerId: number;
    miniGameId: number;
    score: number;
    createdAt: string;
    updatedAt: string;
    createdByUserId: number;
    updatedByUserId: number;
}

export interface IPlayerInMiniGame {
    player: IPlayerDto;
    score: IPlayerMiniGameScoreDto | null;
}

// -----------------------------
// Dialogo de alta/edición jugador
// -----------------------------

interface IPlayerDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    miniGame: IMiniGameDto | null;
    players: IPlayerDto[];
    player: IPlayerDto | null; // para edición
    onClose: () => void;
    onSaved: () => void;
}

interface IPlayerDialogState {
    modeType: 'new' | 'existing'; // solo relevante en create
    selectedPlayerId: number | null;
    firstName: string;
    lastName: string;
    email: string;
    saving: boolean;
    sex: string;
    error: string | null
}

class PlayerInMiniGameDialog extends React.Component<
    IPlayerDialogProps,
    IPlayerDialogState
> {
    public constructor(props: IPlayerDialogProps) {
        super(props);

        const p = props.player;

        this.state = {
            modeType: 'new',
            selectedPlayerId: null,
            firstName: p?.firstName || '',
            lastName: p?.lastName || '',
            email: p?.email || '',
            saving: false,
            sex: p?.sex || '',
            error: null
        };

        this.handleModeChange = this.handleModeChange.bind(this);
        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handlePlayerChange = this.handlePlayerChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: IPlayerDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.mode !== this.props.mode ||
                prevProps.player?.id !== this.props.player?.id)
        ) {
            const p = this.props.player;

            this.setState({
                modeType: 'new',
                selectedPlayerId: null,
                firstName: p?.firstName || '',
                lastName: p?.lastName || '',
                email: p?.email || '',
                sex: p?.sex || '',
                saving: false,
                error: null
            });
        }
    }

    private handleModeChange(
        event: React.ChangeEvent<HTMLInputElement>,
        value: string
    ): void {
        this.setState({ modeType: value as 'new' | 'existing' });
    }

    private handleFieldChange(
        field: 'firstName' | 'lastName' | 'email'
    ) {
        return (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            this.setState({
                [field]: event.target.value
            } as Pick<IPlayerDialogState, typeof field>);
        };
    }

    private handleSexChange = (event: SelectChangeEvent) => {
        this.setState({ sex: event.target.value as string });
    };

    private handlePlayerChange(event: SelectChangeEvent): void {
        const value = event.target.value;
        this.setState({
            selectedPlayerId: value ? Number(value) : null
        });
    }

    private validate(): string | null {
        const { mode } = this.props;
        const { modeType, selectedPlayerId, firstName, lastName } = this.state;

        if (mode === 'create') {
            if (modeType === 'existing') {
                if (!selectedPlayerId) {
                    return 'Debes seleccionar un jugador existente.';
                }
            } else {
                if (!firstName.trim()) return 'El nombre es obligatorio.';
                if (!lastName.trim()) return 'Los apellidos son obligatorios.';
            }
        } else {
            if (!firstName.trim()) return 'El nombre es obligatorio.';
            if (!lastName.trim()) return 'Los apellidos son obligatorios.';
        }

        if (!this.props.miniGame) {
            return 'No se ha encontrado el minijuego.';
        }

        return null;
    }

    private getCurrentUserId(): number {
        try {
            const userJson = localStorage.getItem('vg_user');
            if (!userJson) return 0;
            const user = JSON.parse(userJson);
            return typeof user.id === 'number' ? user.id : 0;
        } catch {
            return 0;
        }
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        if (!this.props.miniGame) {
            this.setState({ error: 'No se ha encontrado el minijuego.' });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const { mode, miniGame } = this.props;
            const {
                modeType,
                selectedPlayerId,
                firstName,
                lastName,
                email,
                sex
            } = this.state;

            const tournamentId = miniGame.tournamentId;
            const currentUserId = this.getCurrentUserId();

            if (mode === 'create') {
                let playerId: number;

                if (modeType === 'existing') {
                    if (!selectedPlayerId) {
                        throw new Error('Debes seleccionar un jugador.');
                    }
                    playerId = selectedPlayerId;
                } else {
                    // Crear jugador nuevo
                    const createPlayerBody = {
                        tournamentId,
                        firstName,
                        lastName,
                        email,
                        sex
                    };

                    const playerResponse = await Api.post('/api/Players', createPlayerBody);
                    if (!playerResponse.ok) {
                        throw new Error('No se pudo crear el jugador.');
                    }

                    const createdPlayer: IPlayerDto = await playerResponse.json();
                    playerId = createdPlayer.id;
                }

                // Crear asociación jugador–minijuego con score = 0
                const createScoreBody = {
                    tournamentId,
                    playerId,
                    miniGameId: miniGame.id,
                    score: 0,
                    sex,
                    createdByUserId: currentUserId,
                    updatedByUserId: currentUserId
                };

                const scoreResponse = await Api.post(
                    '/api/PlayerMiniGameScores',
                    createScoreBody
                );
                if (!scoreResponse.ok) {
                    throw new Error('No se pudo asociar el jugador al minijuego.');
                }

                this.props.onSaved();
            } else {
                // Editar datos del jugador
                const p = this.props.player;
                if (!p) {
                    throw new Error('No se ha encontrado el jugador.');
                }

                const updateBody = {
                    tournamentId,
                    firstName,
                    lastName,
                    email,
                    sex
                };

                const updateResponse = await Api.put(
                    `/api/Players/${p.id}`,
                    updateBody
                );

                if (!updateResponse.ok) {
                    throw new Error('No se pudieron actualizar los datos del jugador.');
                }

                this.props.onSaved();
            }
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar el jugador.'
            });
        }
    }

    private getPlayerLabel(p: IPlayerDto): string {
        const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
        if (fullName && p.email) return `${fullName} (${p.email})`;
        if (fullName) return fullName;
        return p.email || `Jugador ${p.id}`;
    }

    public render(): React.ReactElement<IPlayerDialogProps> {
        const { open, onClose, mode, players } = this.props;
        const {
            modeType,
            selectedPlayerId,
            firstName,
            lastName,
            email,
            saving,
            error,
            sex
        } = this.state;

        const playersOptions = players.map((x: IPlayerDto) => {
            return {
                label: x.lastName != null && x.lastName.trim() != '' ? `${x.lastName?.trim()}, ${x.firstName}` : `${x.firstName}`,
                id: x.id
            }
        });

        const title =
            mode === 'create' ? 'Nuevo jugador en el minijuego' : 'Editar jugador';

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        {mode === 'create' && (
                            <FormControl component="fieldset">
                                <RadioGroup
                                    row
                                    value={modeType}
                                    onChange={this.handleModeChange}
                                >
                                    <FormControlLabel
                                        value="new"
                                        control={<Radio />}
                                        label="Nuevo jugador"
                                    />
                                    <FormControlLabel
                                        value="existing"
                                        control={<Radio />}
                                        label="Jugador existente"
                                    />
                                </RadioGroup>
                            </FormControl>
                        )}

                        {mode === 'create' && modeType === 'existing' && (
                            <Autocomplete
                                fullWidth
                                options={playersOptions}
                                renderInput={(params) => <TextField {...params} label="Jugador" />}
                            />
                        )}

                        {!(mode === 'create' && modeType === 'existing') && (
                            <>
                                <TextField
                                    label="Nombre"
                                    fullWidth
                                    value={firstName}
                                    onChange={this.handleFieldChange('firstName')}
                                    required
                                />
                                <TextField
                                    label="Apellidos"
                                    fullWidth
                                    value={lastName}
                                    onChange={this.handleFieldChange('lastName')}
                                    required
                                />
                                <FormControl fullWidth>
                                    <InputLabel id="sex-label">Sexo</InputLabel>
                                    <Select
                                        labelId="sex-label"
                                        label="Sexo"
                                        value={sex}
                                        onChange={this.handleSexChange}
                                    >
                                        <MenuItem value="">
                                            <em>Sin especificar</em>
                                        </MenuItem>
                                        <MenuItem value="H">Hombre</MenuItem>
                                        <MenuItem value="M">Mujer</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Email"
                                    fullWidth
                                    value={email}
                                    onChange={this.handleFieldChange('email')}
                                />
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={this.handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Diálogo de puntuación
// -----------------------------

interface IScoreDialogProps {
    open: boolean;
    miniGame: IMiniGameDto | null;
    player: IPlayerDto | null;
    scoreEntry: IPlayerMiniGameScoreDto | null;
    onClose: () => void;
    onSaved: () => void;
}

interface IScoreDialogState {
    score: string;
    saving: boolean;
    error: string | null;
}

class PlayerScoreDialog extends React.Component<
    IScoreDialogProps,
    IScoreDialogState
> {
    public constructor(props: IScoreDialogProps) {
        super(props);

        this.state = {
            score: props.scoreEntry ? String(props.scoreEntry.score) : '',
            saving: false,
            error: null
        };

        this.handleScoreChange = this.handleScoreChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: IScoreDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.scoreEntry?.id !== this.props.scoreEntry?.id)
        ) {
            this.setState({
                score: this.props.scoreEntry
                    ? String(this.props.scoreEntry.score)
                    : '',
                saving: false,
                error: null
            });
        }
    }

    private handleScoreChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ score: event.target.value });
    }

    private getCurrentUserId(): number {
        try {
            const userJson = localStorage.getItem('vg_user');
            if (!userJson) return 0;
            const user = JSON.parse(userJson);
            return typeof user.id === 'number' ? user.id : 0;
        } catch {
            return 0;
        }
    }

    private validate(): string | null {
        const { score } = this.state;
        if (!score.trim()) return 'La puntuación es obligatoria.';

        const value = Number(score);
        if (Number.isNaN(value)) return 'La puntuación debe ser un número.';
        return null;
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        const { miniGame, player, scoreEntry } = this.props;
        if (!miniGame || !player) {
            this.setState({ error: 'No se ha encontrado el minijuego o el jugador.' });
            return;
        }

        const scoreValue = Number(this.state.score);
        const userId = this.getCurrentUserId();

        this.setState({ saving: true, error: null });

        try {
            if (!scoreEntry) {
                // Crear nueva entrada de score
                const createBody = {
                    tournamentId: miniGame.tournamentId,
                    playerId: player.id,
                    miniGameId: miniGame.id,
                    score: scoreValue,
                    createdByUserId: userId,
                    updatedByUserId: userId
                };

                const response = await Api.post(
                    '/api/PlayerMiniGameScores',
                    createBody
                );
                if (!response.ok) {
                    throw new Error('No se pudo guardar la puntuación.');
                }
            } else {
                const updateBody = {
                    score: scoreValue,
                    updatedByUserId: userId
                };

                const response = await Api.put(
                    `/api/PlayerMiniGameScores/${scoreEntry.id}`,
                    updateBody
                );
                if (!response.ok) {
                    throw new Error('No se pudo actualizar la puntuación.');
                }
            }

            this.props.onSaved();
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar la puntuación.'
            });
        }
    }

    public render(): React.ReactElement<IScoreDialogProps> {
        const { open, onClose, miniGame, player } = this.props;
        const { score, saving, error } = this.state;

        const playerName = player
            ? `${player.firstName || ''} ${player.lastName || ''}`.trim() ||
            player.email ||
            `Jugador ${player.id}`
            : '';

        const title = `Puntuación de ${playerName}`;

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {title}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {miniGame && (
                            <Alert icon={<SportsGolfOutlined />} severity='info'>
                                {miniGame.name || `#${miniGame.id}`}
                            </Alert>
                        )}

                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        <TextField
                            label="Puntuación"
                            fullWidth
                            type="number"
                            value={score}
                            onChange={this.handleScoreChange}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={this.handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Página principal de detalle
// -----------------------------

export interface IMiniGameDetailPageProps { }

export interface IMiniGameDetailPageState {
    miniGame: IMiniGameDto | null;
    tournament: ITournamentDto | null;
    playersInGame: IPlayerInMiniGame[];
    allPlayers: IPlayerDto[];
    loading: boolean;
    error: string | null;
    searchText: string;

    playerDialogOpen: boolean;
    playerDialogMode: 'create' | 'edit';
    selectedPlayer: IPlayerDto | null;

    scoreDialogOpen: boolean;
    scoreDialogPlayer: IPlayerDto | null;
    scoreDialogEntry: IPlayerMiniGameScoreDto | null;
    playersOptions: {label: string, id: number}[];
}

export default class MiniGameDetailPage extends React.Component<
    IMiniGameDetailPageProps,
    IMiniGameDetailPageState
> {
    public constructor(props: IMiniGameDetailPageProps) {
        super(props);

        this.state = {
            miniGame: null,
            tournament: null,
            playersInGame: [],
            allPlayers: [],
            loading: false,
            error: null,
            searchText: '',
            playerDialogOpen: false,
            playerDialogMode: 'create',
            selectedPlayer: null,
            scoreDialogOpen: false,
            scoreDialogPlayer: null,
            scoreDialogEntry: null,
            playersOptions: []
        };

        this.loadData = this.loadData.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleCreatePlayer = this.handleCreatePlayer.bind(this);
        this.handleEditPlayer = this.handleEditPlayer.bind(this);
        this.handleDeletePlayerFromGame = this.handleDeletePlayerFromGame.bind(this);
        this.handlePlayerDialogClose = this.handlePlayerDialogClose.bind(this);
        this.handlePlayerDialogSaved = this.handlePlayerDialogSaved.bind(this);
        this.handleOpenScoreDialog = this.handleOpenScoreDialog.bind(this);
        this.handleScoreDialogClose = this.handleScoreDialogClose.bind(this);
        this.handleScoreDialogSaved = this.handleScoreDialogSaved.bind(this);
    }

    public componentDidMount(): void {
        this.loadData();
    }

    private getMiniGameIdFromQuery(): number | null {
        const params = new URLSearchParams(window.location.search);
        const gid = params.get('gameId');
        if (!gid) return null;
        const value = parseInt(gid, 10);
        return Number.isNaN(value) ? null : value;
    }

    private async loadData(): Promise<void> {
        const miniGameId = this.getMiniGameIdFromQuery();
        if (!miniGameId) {
            this.setState({
                loading: false,
                error: 'No se ha especificado el minijuego (gameId).'
            });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            // 1. Minijuego
            const miniGameResponse = await Api.get(`/api/MiniGames/${miniGameId}`);
            if (!miniGameResponse.ok) {
                throw new Error('No se pudo cargar el minijuego.');
            }
            const miniGame: IMiniGameDto = await miniGameResponse.json();

            // 2. Torneo
            const tournamentResponse = await Api.get(
                `/api/Tournaments/${miniGame.tournamentId}`
            );
            if (!tournamentResponse.ok) {
                throw new Error('No se pudo cargar el torneo del minijuego.');
            }
            const tournament: ITournamentDto = await tournamentResponse.json();

            // 3. Scores del minijuego
            const scoresResponse = await Api.get(
                `/api/PlayerMiniGameScores/by-minigame/${miniGame.id}`
            );
            if (!scoresResponse.ok) {
                throw new Error('No se pudieron cargar las puntuaciones del minijuego.');
            }
            const scores: IPlayerMiniGameScoreDto[] = await scoresResponse.json();

            // 4. Jugadores del torneo
            const playersResponse = await Api.get(
                `/api/Players`
            );

            if (!playersResponse.ok) {
                throw new Error('No se pudieron cargar los jugadores del torneo.');
            }

            const players: IPlayerDto[] = await playersResponse.json();

            // Construimos jugadores inscritos en el minijuego
            // Si hubiera más de un score por jugador, nos quedamos con el más reciente
            const latestScoreByPlayer = new Map<number, IPlayerMiniGameScoreDto>();

            scores.forEach((s) => {
                const existing = latestScoreByPlayer.get(s.playerId);
                if (!existing) {
                    latestScoreByPlayer.set(s.playerId, s);
                    return;
                }
                const existingDate = new Date(existing.updatedAt || existing.createdAt);
                const newDate = new Date(s.updatedAt || s.createdAt);
                if (newDate > existingDate) {
                    latestScoreByPlayer.set(s.playerId, s);
                }
            });

            const playersInGame: IPlayerInMiniGame[] = [];
            latestScoreByPlayer.forEach((score, playerId) => {
                const player = players.find((p) => p.id === playerId);
                if (player) {
                    playersInGame.push({ player, score });
                }
            });

            this.setState({
                miniGame,
                tournament,
                playersInGame,
                allPlayers: players,
                loading: false,
                error: null,
                searchText: ''
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error: error.message || 'Error al cargar los datos del minijuego.'
            });
        }
    }

    // ---------------- Toolbar / búsqueda ----------------

    private handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ searchText: event.target.value });
    }

    private renderToolbar(): React.ReactNode {
        const { searchText } = this.state;

        return (
            <>
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={this.handleCreatePlayer}
                >
                    Nuevo jugador
                </Button>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar jugador..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    // ---------------- Cabecera de minijuego ----------------

    private formatDate(dateString: string): string {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString();
    }
    // ---------------- Lógica jugadores / acciones ----------------

    private matchesSearchPlayer(p: IPlayerDto, term: string): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
        const parts: string[] = [];
        if (fullName) parts.push(fullName);
        if (p.email) parts.push(p.email);

        return parts.some((x) => x.toLowerCase().includes(q));
    }

    private handleCreatePlayer(): void {
        this.setState({
            playerDialogOpen: true,
            playerDialogMode: 'create',
            selectedPlayer: null
        });
    }

    private handleEditPlayer(item: IPlayerInMiniGame): void {
        this.setState({
            playerDialogOpen: true,
            playerDialogMode: 'edit',
            selectedPlayer: item.player
        });
    }

    private async handleDeletePlayerFromGame(
        item: IPlayerInMiniGame
    ): Promise<void> {
        const { score, player } = item;

        if (!score) {
            alert('Este jugador no tiene asociación de puntuación en el minijuego.');
            return;
        }

        const name =
            `${player.firstName || ''} ${player.lastName || ''}`.trim() ||
            player.email ||
            `Jugador ${player.id}`;

        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar a "${name}" de este minijuego?`
        );
        if (!confirmar) return;

        try {
            const response = await Api.delete(
                `/api/PlayerMiniGameScores/${score.id}`
            );

            if (!response.ok) {
                throw new Error('No se pudo eliminar el jugador del minijuego.');
            }

            await this.loadData();
        } catch (error: any) {
            alert(
                error.message ||
                'Error al eliminar la asociación del jugador con el minijuego.'
            );
        }
    }

    private handlePlayerDialogClose(): void {
        this.setState({
            playerDialogOpen: false,
            selectedPlayer: null
        });
    }

    private async handlePlayerDialogSaved(): Promise<void> {
        this.setState({
            playerDialogOpen: false,
            selectedPlayer: null
        });
        await this.loadData();
    }

    private handleOpenScoreDialog(item: IPlayerInMiniGame): void {
        this.setState({
            scoreDialogOpen: true,
            scoreDialogPlayer: item.player,
            scoreDialogEntry: item.score
        });
    }

    private handleScoreDialogClose(): void {
        this.setState({
            scoreDialogOpen: false,
            scoreDialogPlayer: null,
            scoreDialogEntry: null
        });
    }

    private async handleScoreDialogSaved(): Promise<void> {
        this.setState({
            scoreDialogOpen: false,
            scoreDialogPlayer: null,
            scoreDialogEntry: null
        });
        await this.loadData();
    }

    private renderPlayerCard(item: IPlayerInMiniGame): React.ReactNode {
        const { player, score } = item;

        const fullName =
            `${player.firstName || ''} ${player.lastName || ''}`.trim() ||
            player.email ||
            `Jugador ${player.id}`;

        const playerImageUrl = player.sex == 'H' ? '/images/golfista-hombre.jpg' : '/images/golfista-mujer.jpg';

        return (
            <Grid key={player.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 4
                    }}
                >
                    <CardMedia
                        component="img"
                        height="260"
                        image={playerImageUrl}
                        alt={fullName}
                    />

                    <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
                        <Typography variant="h6" fontWeight={500} gutterBottom noWrap>
                            {fullName}
                        </Typography>
                        {score && (
                            <Chip icon={<MilitaryTechOutlined />} size='medium' label={score.score} color="error" sx={{ position: 'absolute', top: '-18px', right: 10, fontWeight: 500 }} />
                        )}

                        {player.email && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                {player.email}
                            </Typography>
                        )}
                    </CardContent>

                    <CardActions
                        sx={{
                            mt: 'auto',
                            justifyContent: 'flex-start',
                            px: 0,
                            pb: 2
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => this.handleEditPlayer(item)}
                        >
                            Editar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => this.handleDeletePlayerFromGame(item)}
                        >
                            Eliminar
                        </Button>

                        <Button
                            size="small"
                            onClick={() => this.handleOpenScoreDialog(item)}
                        >
                            Puntuación
                        </Button>
                    </CardActions>
                </Card>
            </Grid>
        );
    }

    private renderPlayersGrid(): React.ReactNode {
        const { playersInGame, loading, error, searchText } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!playersInGame || playersInGame.length === 0) {
            return (
                <Alert severity='warning'>
                    <Typography variant="body1" color="text.secondary">
                        No hay jugadores inscritos en este minijuego.
                    </Typography>
                </Alert>
            );
        }

        const filtered = playersInGame.filter((item) =>
            this.matchesSearchPlayer(item.player, searchText)
        );

        if (filtered.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay jugadores que coincidan con la búsqueda.
                </Typography>
            );
        }

        return (
            <Grid container spacing={3}>
                {filtered.sort((a, b) => b.score?.score! - a.score?.score!).map((item) => this.renderPlayerCard(item))}
            </Grid>
        );
    }

    public render(): React.ReactElement<IMiniGameDetailPageProps> {
        const {
            miniGame,
            allPlayers,
            playerDialogOpen,
            playerDialogMode,
            selectedPlayer,
            scoreDialogOpen,
            scoreDialogPlayer,
            scoreDialogEntry
        } = this.state;

        const entityTitle =
            miniGame?.name || 'Detalle del minijuego';

        return (
            <>
                <AdminLayout
                    entityTitle={entityTitle}
                    entityIcon={<SportsGolfOutlined sx={{ fontSize: '56px' }} />}
                    entityToolbar={this.renderToolbar()}
                >
                    {this.renderPlayersGrid()}
                </AdminLayout>

                <PlayerInMiniGameDialog
                    open={playerDialogOpen}
                    mode={playerDialogMode}
                    miniGame={miniGame}
                    players={allPlayers}
                    player={selectedPlayer}
                    onClose={this.handlePlayerDialogClose}
                    onSaved={this.handlePlayerDialogSaved}
                />

                <PlayerScoreDialog
                    open={scoreDialogOpen}
                    miniGame={miniGame}
                    player={scoreDialogPlayer}
                    scoreEntry={scoreDialogEntry}
                    onClose={this.handleScoreDialogClose}
                    onSaved={this.handleScoreDialogSaved}
                />
            </>
        );
    }
}
